import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import TableCard from '../components/TableCard';
import QRModal from '../components/QRModal';

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3500);
  }, []);
  return { toasts, addToast };
};

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const IconXCircle = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);
const IconInfo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Toast = ({ toasts }) => (
  <div className="toast-container">
    {toasts.map((t) => (
      <div key={t.id} className={`toast ${t.type}`}>
        <span className="toast-icon">{t.type === 'success' ? <IconCheck /> : t.type === 'error' ? <IconXCircle /> : <IconInfo />}</span>
        <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
      </div>
    ))}
  </div>
);

const TablesPage = () => {
  const { user } = useAuth();
  const { toasts, addToast } = useToast();
  const storeId = user?.storeId || user?.store_id;

  const [tables, setTables] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQRTable, setSelectedQRTable] = useState(null);

  // Bulk create modal
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [bulkCount, setBulkCount] = useState(4);
  const [isCreating, setIsCreating] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState(null);

  const fetchTables = useCallback(async () => {
    if (!storeId) {
      setTables([]);
      setIsLoading(false);
      return;
    }
    try {
      const res = await api.get(`/stores/${storeId}/tables`);
      const data = res.data;
      setTables(Array.isArray(data) ? data : data.tables || []);
    } catch {
      setTables([]);
      addToast('데이터를 불러올 수 없습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [storeId, addToast]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const getNextTableNumber = () => {
    if (tables.length === 0) return 1;
    const nums = tables.map((t) => t.tableNumber || t.table_number || 0);
    return Math.max(...nums) + 1;
  };

  const handleBulkCreate = async () => {
    if (bulkCount < 1 || bulkCount > 50) {
      addToast('1~50개 사이로 입력해주세요.', 'error');
      return;
    }
    setIsCreating(true);
    try {
      const startNumber = getNextTableNumber();
      const res = await api.post(`/stores/${storeId}/tables/bulk`, {
        count: bulkCount,
        startNumber,
      });
      const newTables = res.data?.tables || res.data || [];
      setTables((prev) => [...prev, ...(Array.isArray(newTables) ? newTables : [])]);
      addToast(`테이블 ${bulkCount}개가 생성되었습니다!`, 'success');
      setShowBulkCreate(false);
      setBulkCount(4);

      // If response has no actual tables, refetch
      if (!Array.isArray(newTables) || newTables.length === 0) {
        await fetchTables();
      }
    } catch (err) {
      // Mock creation for demo
      if (!storeId || err.message === 'Network Error') {
        const startNum = getNextTableNumber();
        const mockNew = Array.from({ length: bulkCount }, (_, i) => ({
          _id: `mock-${Date.now()}-${i}`,
          tableNumber: startNum + i,
          status: 'empty',
        }));
        setTables((prev) => [...prev, ...mockNew]);
        addToast(`테이블 ${bulkCount}개가 생성되었습니다! (데모)`, 'success');
        setShowBulkCreate(false);
        setBulkCount(4);
      } else {
        addToast(err.response?.data?.message || '테이블 생성에 실패했습니다.', 'error');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handlePaymentComplete = (table) => {
    const tableNum = table.tableNumber || table.table_number;
    setConfirmDialog({
      title: '결제 완료 처리',
      message: `테이블 ${tableNum}번의 결제를 완료 처리하고 테이블을 초기화하시겠습니까?\n정산 후 테이블이 빈 상태로 전환됩니다.`,
      onConfirm: async () => {
        try {
          if (storeId) {
            await api.patch(`/stores/${storeId}/tables/${table._id || table.id}/reset`);
          }
          setTables((prev) =>
            prev.map((t) =>
              (t._id || t.id) === (table._id || table.id)
                ? { ...t, status: 'empty', currentAmount: 0, occupiedAt: null }
                : t
            )
          );
          addToast(`테이블 ${tableNum}번 결제 완료 및 초기화되었습니다.`, 'success');
        } catch {
          addToast('결제 완료 처리에 실패했습니다.', 'error');
        }
        setConfirmDialog(null);
      },
    });
  };

  const handleReset = (table) => {
    setConfirmDialog({
      title: '상태 초기화',
      message: `테이블 ${table.tableNumber || table.table_number}번의 상태를 초기화하시겠습니까?\n현재 주문 및 결제 정보가 초기화됩니다.`,
      onConfirm: async () => {
        try {
          if (storeId) {
            await api.patch(`/stores/${storeId}/tables/${table._id || table.id}/reset`);
          }
          setTables((prev) =>
            prev.map((t) =>
              (t._id || t.id) === (table._id || table.id)
                ? { ...t, status: 'empty', currentAmount: 0, occupiedAt: null }
                : t
            )
          );
          addToast(`테이블 ${table.tableNumber || table.table_number}번이 초기화되었습니다.`, 'success');
        } catch {
          addToast('초기화에 실패했습니다.', 'error');
        }
        setConfirmDialog(null);
      },
    });
  };

  const handleDelete = (table) => {
    setConfirmDialog({
      title: '테이블 삭제',
      message: `테이블 ${table.tableNumber || table.table_number}번을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`,
      danger: true,
      onConfirm: async () => {
        try {
          if (storeId) {
            await api.delete(`/stores/${storeId}/tables/${table._id || table.id}`);
          }
          setTables((prev) => prev.filter((t) => (t._id || t.id) !== (table._id || table.id)));
          addToast(`테이블 ${table.tableNumber || table.table_number}번이 삭제되었습니다.`, 'success');
        } catch {
          addToast('삭제에 실패했습니다.', 'error');
        }
        setConfirmDialog(null);
      },
    });
  };

  const previewNumbers = Array.from({ length: Math.min(bulkCount || 0, 50) }, (_, i) => getNextTableNumber() + i);

  if (isLoading) {
    return (
      <div className="page-container">
        <div style={{ marginBottom: 28 }}>
          <div className="skeleton-line h-24 w-60" />
          <div className="skeleton-line w-40" style={{ height: 12, marginTop: 8 }} />
        </div>
        <div className="table-grid">
          {[0,1,2,3,4,5,6,7].map((i) => (
            <div key={i} className="skeleton-card animate-fade-in-up" style={{ animationDelay: `${i * 40}ms`, padding: 18, height: 140 }}>
              <div className="skeleton-line w-40 h-24" />
              <div className="skeleton-line w-60" style={{ marginTop: 12 }} />
              <div className="skeleton-line w-40" style={{ marginTop: 8, height: 10 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">테이블 관리</h1>
          <p className="page-subtitle">총 {tables.length}개 테이블</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowBulkCreate(true)}>
          + 테이블 추가
        </button>
      </div>

      {/* Status summary chips */}
      {tables.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { status: 'empty', label: '빈 테이블', color: 'var(--text-secondary)' },
            { status: 'occupied', label: '사용 중', color: 'var(--primary)' },
            { status: 'payment_pending', label: '결제 중', color: 'var(--warning)' },
            { status: 'payment_done', label: '결제 완료', color: 'var(--success)' },
          ].map(({ status, label, color }, idx) => {
            const count = tables.filter((t) => (t.status || 'empty') === status).length;
            return (
              <div
                key={status}
                className="animate-fade-in-up"
                style={{
                  padding: '6px 14px',
                  background: 'white',
                  borderRadius: '20px',
                  border: '1px solid var(--border)',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  animationDelay: `${idx * 50}ms`,
                }}
              >
                {label}
                <span style={{ background: color, color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '0.72rem' }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tables grid */}
      {tables.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeLinecap="round"/>
              </svg>
            </div>
            <h3>아직 테이블이 없습니다</h3>
            <p>테이블을 추가하고 QR 코드를 생성하여 고객이 바로 주문할 수 있게 해보세요.</p>
            <button className="btn btn-primary mt-16" onClick={() => setShowBulkCreate(true)}>
              + 첫 번째 테이블 추가하기
            </button>
          </div>
        </div>
      ) : (
        <div className="table-grid">
          {tables.map((table, idx) => (
            <div key={table._id || table.id} className="animate-fade-in-up" style={{ animationDelay: `${idx * 40}ms` }}>
            <TableCard
              table={table}
              onQRClick={(t) => setSelectedQRTable(t)}
              onPaymentComplete={handlePaymentComplete}
              onReset={handleReset}
              onDelete={handleDelete}
            />
            </div>
          ))}
        </div>
      )}

      {/* Bulk Create Modal */}
      {showBulkCreate && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowBulkCreate(false); }}>
          <div className="modal">
            <div className="modal-header">
              <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeLinecap="round"/>
                </svg>
                테이블 추가
              </h3>
              <button className="modal-close" onClick={() => setShowBulkCreate(false)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">테이블 몇 개를 만드시겠어요?</label>
                <input
                  type="number"
                  className="form-input"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  min={1}
                  max={50}
                  style={{ fontSize: '1.2rem', textAlign: 'center', fontWeight: 700 }}
                />
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                  최대 50개까지 한 번에 생성할 수 있습니다.
                </p>
              </div>

              {/* Preview */}
              {bulkCount > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10, color: 'var(--text)' }}>
                    생성될 테이블 번호 미리보기
                  </div>
                  {previewNumbers.length > 0 && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--primary)', fontWeight: 600, marginBottom: 8 }}>
                      테이블 {previewNumbers[0]}번 ~ 테이블 {previewNumbers[previewNumbers.length - 1]}번
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {previewNumbers.slice(0, 20).map((num) => (
                      <div
                        key={num}
                        style={{
                          padding: '6px 10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: 'var(--primary-light)',
                          borderRadius: 8,
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          color: 'var(--primary)',
                          border: '1.5px solid rgba(191,155,48,0.2)',
                        }}
                      >
                        {num}번
                      </div>
                    ))}
                    {bulkCount > 20 && (
                      <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.8rem', color: 'var(--text-tertiary)', padding: '0 8px' }}>
                        +{bulkCount - 20}개 더...
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowBulkCreate(false)}>
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleBulkCreate}
                disabled={isCreating || bulkCount < 1}
              >
                {isCreating ? (
                  <><span className="spinner spinner-sm" /> 생성 중...</>
                ) : (
                  `QR 코드 생성 및 테이블 추가 (${bulkCount}개)`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {selectedQRTable && (
        <QRModal
          table={selectedQRTable}
          storeId={storeId || 'demo-store-id'}
          onClose={() => setSelectedQRTable(null)}
        />
      )}

      {/* Confirm Dialog */}
      {confirmDialog && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDialog(null); }}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3 className="modal-title">{confirmDialog.title}</h3>
              <button className="modal-close" onClick={() => setConfirmDialog(null)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text)', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                {confirmDialog.message}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setConfirmDialog(null)}>
                취소
              </button>
              <button
                className={`btn ${confirmDialog.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={confirmDialog.onConfirm}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesPage;
